import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Check, ExternalLink, Loader2, Phone, User } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  ListTabs,
  SearchablePicker,
  type RecordSource,
} from '@command-center/ui-system';
import { intakeDispositionLabel } from '@command-center/leadloop-core';
import { formatFormFieldValue } from '@command-center/oneup-portal-ui';
import { ApiClientError } from '../lib/api';
import { intakeRecordRoute } from '../lib/intake-routes';
import { ContactLine, formatExactDateTime } from './parts';
import {
  leadloopApi,
  type IntakeItem,
  type IntakeLeadMatch,
  type IntakeMatchCandidate,
} from '../lib/leadloop-api';

const labelClass = 'text-xs font-medium uppercase tracking-wide text-muted-foreground';
export type IntakeMode = 'details' | 'create' | 'link';
type Mode = IntakeMode;

/**
 * Reading a record is two questions, so the popup is two tabs.
 *
 * "Is this worth acting on, and how do I reach them?" is Overview. "What did
 * they actually say?" is the raw record — a call's transcript or a form's
 * complete submitted fields. They used to be one scroll, which meant the
 * summary, the spec grid, and then eighteen raw form fields repeating the name,
 * email, and phone already shown twice above them.
 */
type DetailTab = 'overview' | 'raw';

/**
 * Review an unconfirmed intake and decide what it becomes.
 *
 * Three outcomes share one dialog because they are one decision, not three
 * workflows: the user is answering "what is this?" once. Suggested matches are
 * displayed with their reasons and are NEVER pre-applied — selecting one is an
 * explicit click, because an email match on a shared household line or an
 * info@ address is a different person often enough that a silent merge is
 * unacceptable.
 */
export function IntakeReviewDialog({
  intake,
  open,
  onOpenChange,
  mode: requestedMode,
  onModeChange,
}: {
  intake: IntakeItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: Mode;
  onModeChange: (mode: Mode) => void;
}) {
  const setOpen = onOpenChange;
  const setMode = onModeChange;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Proposed lead fields, seeded from the intake and freely editable.
  const [title, setTitle] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [selectedContactLabel, setSelectedContactLabel] = useState<string | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('overview');
  const [quickNote, setQuickNote] = useState('');

  const detail = useQuery({
    queryKey: ['intake', intake.id],
    queryFn: () => leadloopApi.intakeDetail(intake.id),
    enabled: open,
  });

  // Seed the form once the dialog opens. Deliberately keyed on `open` rather
  // than on the query result so a background refetch never clobbers edits in
  // progress. Mode is owned by the caller (which button was pressed), so it is
  // not reset here.
  useEffect(() => {
    if (!open) return;
    /*
      Left blank rather than seeded with a slice of the message. The generated
      title arrives with the detail query and fills this in below; seeding the
      message here is precisely what produced Leads named after the first 80
      characters of a customer's paragraph.
    */
    setTitle('');
    setContactName(intake.displayName ?? '');
    setEmail(intake.email ?? '');
    setPhone(intake.phone ?? '');
    setCompanyName(intake.company ?? '');
    setSelectedContactId(null);
    setSelectedContactLabel(null);
    setSelectedLeadId(null);
    setDetailTab('overview');
    setQuickNote('');
  }, [open, intake]);

  const matches = detail.data?.matches;
  const suggestedTitle = detail.data?.suggestedTitle;

  /*
    Fill the title once the suggestion lands, and only while the field is still
    untouched — a background refetch must never overwrite what the user typed.
    They can edit it freely before creating; this is a starting point, not a
    verdict.
  */
  const [titleTouched, setTitleTouched] = useState(false);
  useEffect(() => {
    if (!open || titleTouched || !suggestedTitle) return;
    setTitle(suggestedTitle);
  }, [open, titleTouched, suggestedTitle]);
  useEffect(() => {
    if (!open) setTitleTouched(false);
  }, [open]);

  /** One shared success path: refresh the queue, then hand off. */
  const settle = (leadRecordId?: string) => {
    queryClient.invalidateQueries({ queryKey: ['loop'] });
    // Refresh both this detail and the Actionable Intake aggregate so the
    // resolved card leaves the desk immediately.
    queryClient.invalidateQueries({ queryKey: ['intake'] });
    queryClient.invalidateQueries({ queryKey: ['intake-source'] });
    queryClient.invalidateQueries({ queryKey: ['leads'] });
    queryClient.invalidateQueries({ queryKey: ['ai-systems', 'calls'] });
    queryClient.invalidateQueries({ queryKey: ['cascade-form-submissions'] });
    setOpen(false);
    // The card becomes the Lead: go straight into the normal Lead workflow so
    // the user never reviews the same inquiry twice.
    if (leadRecordId) navigate(`/leads/${leadRecordId}`);
  };

  const persistQuickNote = async (): Promise<string | null> => {
    const body = quickNote.trim();
    if (!body) return null;
    await leadloopApi.addIntakeNote(intake.sourceType, intake.sourceId, body);
    return body;
  };

  const saveNote = useMutation({
    mutationFn: persistQuickNote,
    onSuccess: () => {
      setQuickNote('');
      queryClient.invalidateQueries({ queryKey: ['intake-source'] });
      queryClient.invalidateQueries({ queryKey: ['ai-systems', 'calls'] });
      queryClient.invalidateQueries({ queryKey: ['cascade-form-submissions'] });
    },
  });

  const createLead = useMutation({
    mutationFn: async () => {
      await persistQuickNote();
      return leadloopApi.createLeadFromIntake(intake.id, {
        title: titleTouched ? title.trim() : undefined,
        contactName: contactName.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        companyName: companyName.trim() || null,
        contactId: selectedContactId,
      });
    },
    onSuccess: (result) => settle(result.leadRecordId),
  });

  const linkLead = useMutation({
    mutationFn: async () => {
      await persistQuickNote();
      return leadloopApi.linkIntakeToLead(intake.id, selectedLeadId!);
    },
    onSuccess: (result) => settle(result.leadRecordId),
  });

  const dismiss = useMutation({
    mutationFn: async () => {
      const note = await persistQuickNote();
      return leadloopApi.dismissIntake(intake.id, 'not_qualified', note);
    },
    // No lead to open — the card just leaves the queue.
    onSuccess: () => settle(),
  });

  // A resolved intake is a historical record, not a pending decision.
  const resolved = intake.status === 'resolved' || intake.status === 'dismissed';
  const mode: Mode = resolved ? 'details' : requestedMode;

  const active = mode === 'create' ? createLead : mode === 'link' ? linkLead : dismiss;
  const errorMessage = useMemo(() => friendlyError(active.error), [active.error]);

  const canSubmit =
    mode === 'details'
      ? false
      : mode === 'create'
        ? !titleTouched || title.trim().length > 0
        : mode === 'link'
          ? Boolean(selectedLeadId)
          : true;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>
            {resolved ? sourceNoun(intake) : `Review ${sourceNoun(intake).toLowerCase()}`}
          </DialogTitle>
          {/*
            No explanation of where records are stored or how they are written.
            A reviewer is deciding what an inquiry is, not learning the backend.
          */}
          <DialogDescription>
            {resolved
              ? 'The original record, preserved exactly as it arrived.'
              : 'Decide what this is.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/*
            The summary block is context for a decision being made, so it sits
            above the Create / Link / Dismiss forms. On the reading tabs it
            would be a third copy of the caller's name, email, and phone — the
            Overview tab carries them once, at full weight.
          */}
          {mode !== 'details' && <IntakeSummary intake={intake} />}

          {resolved ? (
            <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-[12px] text-muted-foreground">
              Already resolved as{' '}
              <span className="font-medium text-foreground">
                {intakeDispositionLabel(intake.disposition ?? 'resolved')}
              </span>
              . This is a read-only view of the original record.
            </p>
          ) : null}
          {/*
            The mode-selector row that used to sit here is gone. It duplicated
            the footer's actions exactly — Create lead, Link, Dismiss appeared
            twice on one dialog — and it made a row of buttons responsible for
            switching content, so nobody could tell which buttons DID something.
            The footer owns the actions; the body shows whichever one is active.
          */}

          {detail.isLoading && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Looking for possible matches…
            </p>
          )}

          {mode === 'details' && (
            <div className="flex flex-col gap-4">
              <ListTabs
                label={`${sourceNoun(intake)} detail`}
                active={detailTab}
                onChange={(next) => setDetailTab(next as DetailTab)}
                tabs={[
                  { id: 'overview', label: 'Overview' },
                  {
                    id: 'raw',
                    label: intake.sourceType === 'call' ? 'Transcript' : 'All Submitted Fields',
                  },
                ]}
              />
              {detailTab === 'overview' ? (
                <IntakeOverview
                  intake={intake}
                  source={detail.data?.source ?? null}
                  loading={detail.isLoading}
                />
              ) : (
                <IntakeRawRecord
                  intake={intake}
                  source={detail.data?.source ?? null}
                  loading={detail.isLoading}
                />
              )}
            </div>
          )}

          {mode === 'create' && (
            <div className="flex flex-col gap-3">
              <p className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-[12px] text-muted-foreground">
                AI will draft the Lead title and concise request summary when you confirm. Exact email and phone values stay locked to the source record.
              </p>
              <Field label="Lead title" required>
                <Input
                  value={title}
                  placeholder={detail.isLoading ? 'Writing a title…' : 'Name the work being requested'}
                  onChange={(e) => {
                    setTitleTouched(true);
                    setTitle(e.target.value);
                  }}
                />
                {title.trim().length === 0 && !detail.isLoading && (
                  <p className="mt-1 text-[12px] text-destructive">A lead title is required.</p>
                )}
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Contact name">
                  <Input value={contactName} onChange={(e) => setContactName(e.target.value)} />
                </Field>
                <Field label="Company">
                  <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                </Field>
                <Field label="Email">
                  <Input
                    value={email}
                    readOnly={Boolean(intake.email?.trim())}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Field>
                <Field label="Phone">
                  <Input
                    value={phone}
                    readOnly={Boolean(intake.phone?.trim())}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </Field>
              </div>

              <div>
                <p className={labelClass}>Contact</p>
                <p className="mb-2 mt-1 text-[12px] text-muted-foreground">
                  {selectedContactId
                    ? 'This intake will be attached to the selected contact — no new contact is created.'
                    : 'A new contact will be created from the fields above.'}
                </p>
                {matches?.contacts.length ? (
                  <ul className="mb-2 flex flex-col gap-1.5">
                    {matches.contacts.map((candidate) => (
                      <li key={candidate.record.id}>
                        <MatchRow
                          selected={selectedContactId === candidate.record.id}
                          onToggle={() =>
                            setSelectedContactId(
                              selectedContactId === candidate.record.id ? null : candidate.record.id,
                            )
                          }
                          title={candidate.record.fullName}
                          meta={[candidate.record.email, candidate.record.phone]
                            .filter(Boolean)
                            .join(' · ')}
                          reasons={candidate.reasons}
                          score={candidate.score}
                          icon={<User className="h-3.5 w-3.5" aria-hidden />}
                        />
                      </li>
                    ))}
                  </ul>
                ) : null}
                {/*
                  Suggestions are a shortcut, never the only way through. When
                  matching finds nothing (or finds the wrong people) the user
                  can still search every contact without leaving the dialog.
                */}
                <SearchablePicker
                  entityType="contact"
                  source={contactSource}
                  value={selectedContactId ?? ''}
                  valueLabel={selectedContactLabel ?? undefined}
                  onSelect={(id, label) => {
                    setSelectedContactId(id);
                    setSelectedContactLabel(label);
                  }}
                  onClear={() => {
                    setSelectedContactId(null);
                    setSelectedContactLabel(null);
                  }}
                  placeholder={
                    matches?.contacts.length
                      ? 'Or search all contacts…'
                      : 'Search all contacts…'
                  }
                  emptyLabel="No contacts match."
                />
              </div>
            </div>
          )}

          {mode === 'link' && (
            <div className="flex flex-col gap-2">
              <p className="text-[12px] text-muted-foreground">
                Attaching this intake to an existing lead creates no new lead and no new contact.
              </p>
              {matches?.leads.length ? (
                <ul className="mb-1 flex flex-col gap-1.5">
                  {matches.leads.map((candidate: IntakeMatchCandidate<IntakeLeadMatch>) => (
                    <li key={candidate.record.id}>
                      <MatchRow
                        selected={selectedLeadId === candidate.record.id}
                        onToggle={() => {
                          const next =
                            selectedLeadId === candidate.record.id ? null : candidate.record.id;
                          setSelectedLeadId(next);
                        }}
                        title={candidate.record.title}
                        meta={candidate.record.contactName ?? ''}
                        reasons={candidate.reasons}
                        score={candidate.score}
                      />
                    </li>
                  ))}
                </ul>
              ) : null}
              {/*
                Only exact phone/email matches or high-confidence duplicate
                suggestions are eligible for Link Lead.
              */}
              {!detail.isLoading && !matches?.leads.length && (
                <p className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
                  No exact identity match or high-confidence duplicate Lead was found.
                </p>
              )}
            </div>
          )}

          {!resolved && mode === 'details' && (
            <div className="rounded-md border border-border bg-muted/20 p-3">
              <label className="block">
                <span className={labelClass}>Quick note</span>
                <textarea
                  value={quickNote}
                  rows={2}
                  maxLength={10_000}
                  placeholder={`Add a note about this ${sourceNoun(intake).toLowerCase()}…`}
                  onChange={(event) => setQuickNote(event.target.value)}
                  className="mt-1 min-h-16 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>
              <div className="mt-2 flex items-center justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!quickNote.trim() || saveNote.isPending}
                  onClick={() => saveNote.mutate()}
                >
                  {saveNote.isPending ? 'Saving…' : 'Save note'}
                </Button>
              </div>
            </div>
          )}

          {(errorMessage || saveNote.error) && (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2.5 text-[13px] text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {errorMessage ?? friendlyError(saveNote.error)}
            </p>
          )}
        </div>

        {/*
          One action area, one hierarchy: Create Lead is the primary and sits
          at the right edge; Link to Lead and Dismiss are the alternatives; Open
          full record is a utility and sits apart on the left. There is no
          Close button — the dialog's own X in the corner closes it, and a
          second control that does the same thing only adds a decision.

          "Link to Lead" is the phrase everywhere. It used to be "Link to
          existing" in one place and "Link to lead" in another, on the same
          dialog, for the same action.
        */}
        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={() => {
              setOpen(false);
              navigate(intakeRecordRoute(intake.sourceType, intake.sourceId));
            }}
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            Open full record
          </Button>

          {resolved ? null : mode === 'details' ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={dismiss.isPending}
                onClick={() => dismiss.mutate()}
              >
                {dismiss.isPending ? 'Dismissing…' : 'Dismiss'}
              </Button>
              {matches?.leads.length ? (
                <Button type="button" variant="outline" onClick={() => setMode('link')}>
                  Link to {matches.leads[0]!.record.title}
                </Button>
              ) : null}
              <Button type="button" onClick={() => setMode('create')}>
                Create Lead with AI
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {/* Back out of one outcome without closing the whole dialog. */}
              <Button type="button" variant="outline" onClick={() => setMode('details')}>
                Back
              </Button>
              <Button
                type="button"
                disabled={!canSubmit || active.isPending}
                onClick={() => active.mutate()}
                className="gap-1.5"
              >
                {active.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
                {mode === 'create'
                  ? selectedContactId
                    ? 'Create Lead with selected contact'
                    : 'Create Lead with AI'
                  : mode === 'link'
                    ? 'Link to Lead'
                    : 'Dismiss'}
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IntakeSummary({ intake }: { intake: IntakeItem }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 p-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
        <span className="inline-flex items-center gap-1 font-medium text-foreground">
          {intake.sourceType === 'call' ? (
            <Phone className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <User className="h-3.5 w-3.5" aria-hidden />
          )}
          {intake.displayName ?? 'Unknown'}
        </span>
        {intake.email && <span>{intake.email}</span>}
        {intake.phone && <span>{intake.phone}</span>}
        {intake.company && <span>{intake.company}</span>}
      </div>
      {intake.summary && (
        <p className="mt-2 text-[13px] leading-relaxed text-foreground">{intake.summary}</p>
      )}
      {intake.aiRecommendation && (
        <p className="mt-2 text-[12px] text-muted-foreground">
          <span className="font-medium">Suggested:</span> {intake.aiRecommendation}
          {typeof intake.aiConfidence === 'number' &&
            ` (${Math.round(intake.aiConfidence * 100)}% confidence)`}
        </p>
      )}
    </div>
  );
}

/** A suggested match. Selection is always an explicit user action. */
function MatchRow({
  selected,
  onToggle,
  title,
  meta,
  reasons,
  score,
  icon,
}: {
  selected: boolean;
  onToggle: () => void;
  title: string;
  meta?: string;
  reasons: string[];
  score: number;
  icon?: ReactElement;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      className={`flex w-full items-start justify-between gap-3 rounded-md border p-2.5 text-left transition-colors ${
        selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
      }`}
    >
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-[13px] font-medium text-foreground">
          {icon}
          {title}
        </span>
        {meta && <span className="mt-0.5 block text-[12px] text-muted-foreground">{meta}</span>}
        {reasons.length > 0 && (
          <span className="mt-1 block text-[11px] text-muted-foreground">
            {reasons.join(' · ')} — {Math.round(score * 100)}% match
          </span>
        )}
      </span>
      {selected && <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />}
    </button>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className={labelClass}>
        {label}
        {required && <span aria-hidden> *</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export function sourceNoun(intake: IntakeItem): string {
  return intake.sourceType === 'call' ? 'Call' : 'Form entry';
}

function friendlyError(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof ApiClientError) {
    if (error.code === 'feature_disabled') {
      return 'Write actions are not enabled for this install yet.';
    }
    if (error.code === 'intake_contended') {
      return 'Someone else is processing this intake right now. Refresh and try again.';
    }
    if (error.status === 404) {
      return 'This intake no longer exists. It may have already been resolved.';
    }
  }
  return error instanceof Error ? error.message : 'Something went wrong. Please try again.';
}


const contactSource: RecordSource = async (query) => {
  const res = await leadloopApi.contacts(query || undefined);
  return res.items.slice(0, 80).map((contact) => ({
    id: contact.callerContactId ?? contact.id,
    label: contact.fullName,
    sublabel: contact.email ?? contact.phone ?? undefined,
  }));
};

/** Read a string field off the verbatim source row, or null. */
function sourceString(source: Record<string, unknown> | null, key: string): string | null {
  const value = source?.[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function SourceLoading() {
  return (
    <p className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      Loading the full record…
    </p>
  );
}

/**
 * Everything needed to decide what this inquiry is — and nothing that is
 * merely a copy of the raw submission.
 *
 * Who it was and how to reach them leads, because that is the first thing a
 * reader does something with. The spec grid is facts about the contact itself,
 * the summary is what it was about, and the raw record lives one tab across.
 */
function IntakeOverview({
  intake,
  source,
  loading,
}: {
  intake: IntakeItem;
  source: Record<string, unknown> | null;
  loading: boolean;
}) {
  const isCall = intake.sourceType === 'call';
  const str = (key: string) => sourceString(source, key);
  const occurredAt = intake.occurredAt ?? str('submitted_at') ?? intake.createdAt;

  const rows: [string, string | null][] = isCall
    ? [
        ['Received', formatExactDateTime(occurredAt)],
        ['Duration', formatDuration(source?.['duration_seconds'])],
        ['Direction', formatToken(str('direction'))],
        ['Outcome', formatToken(str('ended_reason') ?? str('status'))],
        ['Category', formatToken(str('call_category'))],
        ['Lead grade', formatToken(str('lead_quality'))],
        ['Sentiment', formatToken(str('sentiment'))],
      ]
    : [
        ['Received', formatExactDateTime(occurredAt)],
        ['Form', str('form_name_at_submission')],
        ['Site', str('site_name_at_submission')],
      ];

  return (
    <div className="flex flex-col gap-3">
      {/* The caller, once, at full weight — this replaces the muted metadata
          strip that used to repeat above every mode of this dialog. */}
      <div className="rounded-md border border-border bg-muted/30 p-3">
        <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
          {isCall ? (
            <Phone className="h-4 w-4 shrink-0 text-primary" aria-hidden />
          ) : (
            <User className="h-4 w-4 shrink-0 text-primary" aria-hidden />
          )}
          {intake.displayName ?? (isCall ? 'Unknown caller' : 'No name submitted')}
        </p>
        <ContactLine phone={intake.phone} email={intake.email} className="mt-2" />
        {intake.company && (
          <p className="mt-1.5 text-[12px] text-muted-foreground">{intake.company}</p>
        )}
      </div>

      <SpecGrid rows={rows} />

      {intake.summary && (
        <Callout label={isCall ? 'Call summary' : 'What they wrote'}>{intake.summary}</Callout>
      )}

      {str('next_action') && <Callout label="Suggested next action">{str('next_action')}</Callout>}

      {intake.aiRecommendation && (
        <p className="text-[12px] text-muted-foreground">
          <span className="font-medium">Suggested:</span> {intake.aiRecommendation}
          {typeof intake.aiConfidence === 'number' &&
            ` (${Math.round(intake.aiConfidence * 100)}% confidence)`}
        </p>
      )}

      {isCall && str('recording_url') && (
        <a
          href={str('recording_url')!}
          target="_blank"
          rel="noreferrer"
          className="inline-flex w-fit items-center gap-1.5 text-[13px] font-medium text-primary hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          Open call recording
        </a>
      )}

      {!isCall && str('source_page_url') && (
        <a
          href={str('source_page_url')!}
          target="_blank"
          rel="noreferrer"
          className="inline-flex w-fit items-center gap-1.5 text-[13px] font-medium text-primary hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          Open the page it was submitted from
        </a>
      )}

      {loading && !source && <SourceLoading />}
    </div>
  );
}

/**
 * The verbatim record: a call's transcript, or every field the visitor filled
 * in. This is where the exhaustive copy belongs — repeating the name, email,
 * and phone here is correct, because this tab is the original document.
 */
function IntakeRawRecord({
  intake,
  source,
  loading,
}: {
  intake: IntakeItem;
  source: Record<string, unknown> | null;
  loading: boolean;
}) {
  if (loading && !source) return <SourceLoading />;
  if (!source) {
    return (
      <p className="py-4 text-sm text-muted-foreground">
        The full record could not be loaded. The Overview is still accurate.
      </p>
    );
  }

  if (intake.sourceType === 'call') {
    const transcript = sourceString(source, 'transcript');
    if (!transcript) {
      return (
        <p className="py-4 text-sm text-muted-foreground">
          No transcript was captured for this call.
        </p>
      );
    }
    return (
      <pre className="max-h-[52vh] overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted/30 p-3 font-sans text-[13px] leading-relaxed text-foreground">
        {transcript}
      </pre>
    );
  }

  const fields = (source['fields_by_label'] ?? {}) as Record<string, unknown>;
  const entries = Object.entries(fields)
    .map(([label, value]) => [label, formatFormFieldValue(value)] as const)
    .filter(([, value]) => Boolean(value));

  if (entries.length === 0) {
    return <p className="py-4 text-sm text-muted-foreground">No fields were captured.</p>;
  }

  return (
    <dl className="max-h-[52vh] divide-y divide-border overflow-y-auto rounded-md border border-border">
      {entries.map(([label, value]) => (
        <div key={label} className="grid grid-cols-[minmax(90px,32%)_1fr] gap-3 px-3 py-2">
          <dt className="text-[12px] text-muted-foreground">{label}</dt>
          <dd className="whitespace-pre-wrap break-words text-[13px] text-foreground">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function SpecGrid({ rows }: { rows: [string, string | null][] }) {
  const present = rows.filter(([, value]) => value);
  if (present.length === 0) return null;
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-md border border-border p-3 sm:grid-cols-3">
      {present.map(([label, value]) => (
        <div key={label}>
          <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
          <dd className="text-[13px] text-foreground">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Callout({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 p-3">
      <p className={labelClass}>{label}</p>
      <p className="mt-1 text-[13px] leading-relaxed text-foreground">{children}</p>
    </div>
  );
}

function formatToken(value: string | null): string | null {
  if (!value) return null;
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[_\s]+/)
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
}

function formatDuration(value: unknown): string | null {
  const seconds = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}
